/**
 * Iter 299 — Step: Lifestyle (Mahlzeiten-Rhythmus, Koch-Zeit, Budget)
 *
 * Ziel: KI passt Rezepte an den Alltag des Kunden an —
 * Vielbeschäftigte bekommen Quick-Recipes, Genießer dürfen schmoren,
 * Sparfüchse essen Linsen statt Lachs.
 */
import React from 'react';
import { Clock, Wallet, UtensilsCrossed } from 'lucide-react';
import { Label } from '@/components/ui/label';

const MEAL_PATTERNS = [
  { id: '3_meals', label: '3 Mahlzeiten', desc: 'Klassisch: Frühstück, Mittag, Abendessen' },
  { id: '3_2_snacks', label: '3 + 2 Snacks', desc: 'Hauptmahlzeiten plus Vor- & Nachmittagssnack' },
  { id: 'if_16_8', label: 'Intervallfasten 16:8', desc: 'Essensfenster 8 Std (z.B. 12–20 Uhr)' },
  { id: 'if_18_6', label: 'Intervallfasten 18:6', desc: 'Essensfenster 6 Std' },
  { id: 'omad', label: 'OMAD', desc: 'Eine Mahlzeit pro Tag' },
];

const COOKING_TIMES = [
  { id: 'quick', label: 'Schnell', desc: '< 15 Min — Bowls, Wraps, One-Pan' },
  { id: 'medium', label: 'Normal', desc: '15–30 Min — Standard-Rezepte' },
  { id: 'extensive', label: 'Genuss', desc: '> 30 Min — Schmoren, Backen, Slow-Cook' },
];

const BUDGET_LEVELS = [
  { id: 'low', label: 'Sparsam', desc: 'Linsen, Hähnchen, Saison — günstig & gesund' },
  { id: 'normal', label: 'Normal', desc: 'Ausgewogener Mix' },
  { id: 'premium', label: 'Premium', desc: 'Lachs, Filet, Bio, Trüffel erlaubt' },
];

const StepLifestyle = ({ prefs, setPrefs }) => {
  return (
    <div className="space-y-6" data-testid="onboarding-step-lifestyle">
      <div className="text-center mb-4">
        <UtensilsCrossed className="w-12 h-12 text-yellow-500 mx-auto mb-3" />
        <h3 className="text-xl font-bold text-white">Dein Alltag</h3>
        <p className="text-zinc-400 text-sm">
          Damit die KI Rezepte vorschlägt, die wirklich in dein Leben passen
        </p>
      </div>

      {/* Mahlzeiten-Rhythmus */}
      <div>
        <Label className="text-white mb-2 block text-sm flex items-center gap-2">
          <Clock className="w-4 h-4 text-yellow-400" />
          Mahlzeiten-Rhythmus
        </Label>
        <div className="space-y-2">
          {MEAL_PATTERNS.map(p => (
            <button
              key={p.id}
              data-testid={`meal-pattern-${p.id}`}
              onClick={() => setPrefs({ ...prefs, meal_pattern: p.id })}
              className={`w-full p-3 rounded-lg border-2 text-left transition-all ${
                (prefs.meal_pattern || '3_meals') === p.id
                  ? 'border-yellow-500 bg-yellow-500/10'
                  : 'border-zinc-700 hover:border-zinc-600 bg-zinc-800/50'
              }`}
            >
              <p className="text-sm font-medium text-white">{p.label}</p>
              <p className="text-xs text-zinc-400 mt-0.5">{p.desc}</p>
            </button>
          ))}
        </div>
      </div>

      {/* Koch-Zeit */}
      <div>
        <Label className="text-white mb-2 block text-sm flex items-center gap-2">
          <Clock className="w-4 h-4 text-yellow-400" />
          Wie viel Zeit nimmst du fürs Kochen?
        </Label>
        <div className="grid grid-cols-3 gap-2">
          {COOKING_TIMES.map(c => (
            <button
              key={c.id}
              data-testid={`cooking-${c.id}`}
              onClick={() => setPrefs({ ...prefs, cooking_time: c.id })}
              className={`p-3 rounded-lg border-2 text-left transition-all ${
                (prefs.cooking_time || 'medium') === c.id
                  ? 'border-yellow-500 bg-yellow-500/10'
                  : 'border-zinc-700 hover:border-zinc-600 bg-zinc-800/50'
              }`}
            >
              <p className="text-sm font-medium text-white">{c.label}</p>
              <p className="text-xs text-zinc-400 mt-0.5">{c.desc}</p>
            </button>
          ))}
        </div>
      </div>

      {/* Budget */}
      <div>
        <Label className="text-white mb-2 block text-sm flex items-center gap-2">
          <Wallet className="w-4 h-4 text-yellow-400" />
          Budget-Level
        </Label>
        <div className="grid grid-cols-3 gap-2">
          {BUDGET_LEVELS.map(b => (
            <button
              key={b.id}
              data-testid={`budget-${b.id}`}
              onClick={() => setPrefs({ ...prefs, budget_level: b.id })}
              className={`p-3 rounded-lg border-2 text-left transition-all ${
                (prefs.budget_level || 'normal') === b.id
                  ? 'border-yellow-500 bg-yellow-500/10'
                  : 'border-zinc-700 hover:border-zinc-600 bg-zinc-800/50'
              }`}
            >
              <p className="text-sm font-medium text-white">{b.label}</p>
              <p className="text-xs text-zinc-400 mt-0.5">{b.desc}</p>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export { MEAL_PATTERNS, COOKING_TIMES, BUDGET_LEVELS };
export default StepLifestyle;
